import { faArrowRight } from "@awesome.me/kit-c10ef98ed3/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@mui/material";
import { useTranslations } from "next-intl";
import { useRef, useState, useEffect } from "react";

export interface NavigationButtonsProps {
  canGoNext: boolean;
  canGoBack: boolean;
  isLastQuestion: boolean;
  isAutoAdvancing: boolean;
  goNext: () => void;
  goBack: () => void;
  onSkip?: () => void;
  nextLabelKey?: string;
}

export function NavigationButtons({
  canGoNext,
  canGoBack,
  isLastQuestion,
  isAutoAdvancing,
  goNext,
  goBack,
  onSkip,
  nextLabelKey,
}: NavigationButtonsProps) {
  const tQuestionnaire = useTranslations("questionnaire");
  const tAria = useTranslations("aria");

  const navBarRef = useRef<HTMLElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsAtBottom(entry.isIntersecting),
      { threshold: 1.0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const navBar = navBarRef.current;
    if (!navBar) return;

    const root = navBar.closest(".sap") as HTMLElement | null;
    if (!root) return;

    const updateHeight = () => {
      root.style.setProperty(
        "--questionnaire-nav-bar-height",
        `${navBar.getBoundingClientRect().height}px`,
      );
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      return () => {
        root.style.removeProperty("--questionnaire-nav-bar-height");
      };
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(navBar);

    return () => {
      observer.disconnect();
      root.style.removeProperty("--questionnaire-nav-bar-height");
    };
  }, []);

  return (
    <>
      <nav
        ref={navBarRef}
        className={`questionnaire-nav-bar${!isAtBottom ? " questionnaire-nav-bar--scrolled" : ""}`}
        aria-label={tAria("navigation-label")}
      >
        <div className="questionnaire-nav-bar-inner">
          {canGoBack && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={goBack}
              className="questionnaire-nav-button"
            >
              {tQuestionnaire("back")}
            </Button>
          )}

          <div className="questionnaire-nav-bar-right">
            {onSkip && (
              <Button
                variant="text"
                color="secondary"
                onClick={onSkip}
                className="questionnaire-nav-button questionnaire-nav-button--skip"
              >
                {tQuestionnaire("skip")}
              </Button>
            )}

            <div aria-live="polite">
              {canGoNext && !isAutoAdvancing && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={goNext}
                  className="questionnaire-nav-button"
                >
                  {tQuestionnaire(
                    nextLabelKey ?? (isLastQuestion ? "see-results" : "next"),
                  )}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="nav-button-arrow"
                    aria-hidden="true"
                  />
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>
      <div ref={sentinelRef} aria-hidden="true" />
    </>
  );
}
